import React, { useState, useEffect } from 'react'

import classNames from 'classnames'

import arrow from '../../assets/svg/chevron_right.svg'

import CloseModal from './components/CloseModal'
import AddNewSafetyTeam from './components/AddNewSafetyTeam'
import { ActiveSafetyTeamTable } from './components/ActiveSafetyTeamTable'
import { ArchivedSafetyTeamTable } from './components/ArchivedSafetyTeamTable'
import ConfirmModal from './components/ConfirmModal'
import ExportButton from './components/ExportButton'

import { useSafetyTeam } from './hooks/useSafetyTeam'
import { usePeople } from './hooks/usePeople'

import styles from './SafetyTeam.module.scss'

export const SafetyTeam = () => {
  const [isTableHidden, setIsTableHidden] = useState({
    activeTable: false,
    archivedTable: true,
  })

  const { availablePeople } = usePeople()

  const {
    activeRecords,
    archivedRecords,
    isLoading,
    confirmModal,
    handleAddNewRecord,
    handleSave,
    handleEdit,
    handleDelete,
    handleFieldChange,
    handleDriverSelect,
    handleDocumentChange,
    handleReopenRecord,
    handleDeleteArchived,
    handleConfirmAction,
    handleCloseModal
  } = useSafetyTeam()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        const editingRecord = activeRecords.find((record) => record.isEditing)
        if (editingRecord) {
          handleSave(editingRecord.id)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [activeRecords, handleSave])

  const toggleTable = (tableName: 'activeTable' | 'archivedTable') => {
    setIsTableHidden((prev) => ({
      ...prev,
      [tableName]: !prev[tableName],
    }))
  }

  return (
    <section className={styles['safety-team-wrapper']}>
      <div className={styles['safety-team']}>
        <h1>Safety Team</h1>

        <CloseModal />

        <div className={styles.headerButtons}>
          <ExportButton
            activeRecords={activeRecords}
            archivedRecords={archivedRecords}
          />

          <AddNewSafetyTeam
            onAddNew={handleAddNewRecord}
          />
        </div>

        <div
          className={classNames({
            [styles.expandedSection]: !isTableHidden.activeTable,
            [styles.collapsedSection]: isTableHidden.activeTable,
          })}
        >
          <h3
            className={styles.sectionHeader}
            onClick={() => toggleTable('activeTable')}
          >
            Active ({activeRecords.length})
            <span
              className={classNames(styles.arrow, {
                [styles.arrowRotated]: !isTableHidden.activeTable,
              })}
            >
              <img src={arrow} alt="toggle" />
            </span>
          </h3>

          <ActiveSafetyTeamTable
            records={activeRecords}
            onSave={handleSave}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onFieldChange={handleFieldChange}
            onDriverSelect={handleDriverSelect}
            onDocumentChange={handleDocumentChange}
            availablePeople={availablePeople}
            isTableHidden={isTableHidden.activeTable}
            isLoading={isLoading}
          />
        </div>

        <div
          className={classNames({
            [styles.expandedSection]: !isTableHidden.archivedTable,
            [styles.collapsedSection]: isTableHidden.archivedTable,
          })}
        >
          <h3
            className={styles.sectionHeader}
            onClick={() => toggleTable('archivedTable')}
          >
            Closed ({archivedRecords.length})
            <span
              className={classNames(styles.arrow, {
                [styles.arrowRotated]: !isTableHidden.archivedTable,
              })}
            >
              <img src={arrow} alt="toggle" />
            </span>
          </h3>

          <ArchivedSafetyTeamTable
            records={archivedRecords}
            onReopen={handleReopenRecord}
            onDelete={handleDeleteArchived}
            isTableHidden={isTableHidden.archivedTable}
            isLoading={isLoading}
          />
        </div>

        <ConfirmModal
          open={confirmModal.open}
          onClose={handleCloseModal}
          onConfirm={handleConfirmAction}
          actionType={confirmModal.action}
        />
      </div>
    </section>
  )
}
